// Bibliothèque d'exercices du coach — page dédiée accessible depuis l'onglet
// "Exercices" de la sidebar. Permet de voir, créer, modifier et changer la
// photo des exercices du coach. Les exercices de base (IdLocal 1..135) ne
// sont ni modifiables ni supprimables (règle héritée de l'app mobile).
import React, { useMemo, useRef, useState, useEffect } from "react";
import ExerciseThumb from "../ExerciseThumb";
import {
	createExercise,
	updateExercise,
	uploadExerciseImage,
	deleteExercise,
	importCoachExerciseToStudent,
	getStudentExercises,
} from "../../services/authService";
import { getPhotoUrl } from "../../services/apiConfig";
import { resolveExercisePicto } from "../../utils/exercisePicto";

const MAX_DEFAULT_ID_LOCAL = 185;
const isDefaultExercise = (ex) => {
	const idLocal = Number(ex?.idLocal ?? ex?.IdLocal ?? 0);
	return idLocal >= 1 && idLocal <= MAX_DEFAULT_ID_LOCAL;
};

const MUSCLE_LABELS = [
	"Pectoraux", "Epaules", "Dorsaux", "Trapezes", "Biceps", "Triceps", "Avant-bras",
	"Cou", "Fessiers", "Quadriceps", "Ischio-jambiers", "Mollets", "Abdominaux",
	"Lombaires", "Adducteurs", "Abducteurs",
];

const MUSCLE_FILTERS = [
	{ key: "all", label: "Tous" },
	{ key: "pecs", label: "Pecs", muscles: ["Pectoraux"] },
	{ key: "dos", label: "Dos", muscles: ["Dorsaux", "Trapezes"] },
	{ key: "jambes", label: "Jambes", muscles: ["Quadriceps", "Ischio-jambiers", "Mollets", "Fessiers", "Adducteurs", "Abducteurs"] },
	{ key: "bras", label: "Bras", muscles: ["Biceps", "Triceps", "Avant-bras"] },
	{ key: "epaules", label: "Épaules", muscles: ["Epaules"] },
	{ key: "abdos", label: "Abdos", muscles: ["Abdominaux", "Lombaires"] },
];

function musclesArrayToNames(muscles) {
	if (!muscles || muscles.length === 0) return [];
	return muscles.map((v, i) => (v === 1 ? MUSCLE_LABELS[i] : null)).filter(Boolean);
}

function musclesNamesToArray(names) {
	// Retourne un tableau de 16 (0/1) dans le même ordre que MUSCLE_LABELS
	const set = new Set(names);
	return MUSCLE_LABELS.map((label) => (set.has(label) ? 1 : 0));
}

function fileToBase64(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result);
		reader.onerror = reject;
		reader.readAsDataURL(file);
	});
}

// ─── Modale édition / création ───
function ExerciseFormModal({ userId, exercise, onClose, onSaved }) {
	const isEditing = !!exercise;
	const isDefault = isEditing && isDefaultExercise(exercise);

	const [nom, setNom] = useState(exercise?.nomExercice || "");
	const [description, setDescription] = useState(exercise?.comExercice || "");
	const [tempsRepos, setTempsRepos] = useState(exercise?.tempsRepos ?? 90);
	const [muscles, setMuscles] = useState(() => musclesArrayToNames(exercise?.muscles || []));
	// imageUrl : null = pas de modif, "" = supprimer, string = nouvelle URL relative
	const [imageUrl, setImageUrl] = useState(null);
	// previewUrl : ce qu'on affiche dans la vignette de la modale. Fallback :
	//   1) photo custom uploadée par l'user (exercise.imageUrl)
	//   2) picto par défaut (résolu par nom via /picto-exo/)
	//   3) null (exo custom sans nom matching de picto par défaut)
	// resolveExercisePicto gère les 3 cas et couvre notamment les exos de base
	// (IdLocal 1..135) qui n'ont pas d'imageUrl mais un picto bundle.
	const [previewUrl, setPreviewUrl] = useState(resolveExercisePicto(exercise));
	const [uploading, setUploading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const fileRef = useRef(null);

	const toggleMuscle = (label) => {
		if (isDefault) return;
		setMuscles((prev) => (prev.includes(label) ? prev.filter((m) => m !== label) : [...prev, label]));
	};

	const handlePickPhoto = () => fileRef.current?.click();

	const handleFile = async (e) => {
		const file = e.target.files?.[0];
		if (!file) return;
		if (file.size > 10 * 1024 * 1024) { setError("Photo trop lourde (max 10 Mo)"); return; }
		setUploading(true); setError("");
		try {
			const base64 = await fileToBase64(file);
			const res = await uploadExerciseImage(base64);
			setUploading(false);
			if (!res.success) { setError(res.error || "Upload échoué"); return; }
			setImageUrl(res.imageUrl);
			setPreviewUrl(getPhotoUrl(res.imageUrl));
		} catch {
			setUploading(false);
			setError("Erreur lors de la lecture du fichier");
		}
	};

	const handleRemovePhoto = () => {
		setImageUrl(""); // vider côté serveur
		setPreviewUrl(null);
	};

	const handleSave = async () => {
		if (!nom.trim()) { setError("Le nom est requis"); return; }
		setSaving(true); setError("");
		const musclesArr = musclesNamesToArray(muscles);
		let res;
		if (isEditing) {
			res = await updateExercise(userId, exercise.id, nom.trim(), description, musclesArr, tempsRepos, imageUrl);
		} else {
			res = await createExercise(userId, nom.trim(), description, musclesArr, tempsRepos, imageUrl || null);
		}
		setSaving(false);
		if (res.success) onSaved?.();
		else setError(res.error || "Erreur");
	};

	return (
		<div className="mens-modal-overlay" onClick={onClose}>
			<div className="mens-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, width: "94%" }}>
				<div className="mens-modal-header">
					<h3>{isEditing ? "Modifier l'exercice" : "Nouvel exercice"}</h3>
					<button className="mens-modal-close" onClick={onClose}>×</button>
				</div>

				{isDefault && (
					<div style={{ padding: "10px 12px", background: "rgba(248,123,27,0.08)", border: "1px solid rgba(248,123,27,0.25)", borderRadius: 8, fontSize: 12, color: "var(--ink-soft)", marginTop: 8 }}>
						Exercice de la base par défaut : nom, description, photo et groupes musculaires sont verrouillés. Seul le <strong>temps de repos</strong> peut être ajusté.
					</div>
				)}

				<div style={{ display: "flex", gap: 14, marginTop: 12 }}>
					{/* Colonne photo */}
					<div style={{ flexShrink: 0, width: 120 }}>
						<div style={{
							width: 120, height: 120, borderRadius: 12,
							border: "1px dashed var(--line-soft)",
							background: previewUrl ? `url(${previewUrl}) center/cover` : "rgba(20,30,27,0.04)",
							display: "flex", alignItems: "center", justifyContent: "center",
							color: "var(--ink-faint)", fontSize: 11,
						}}>
							{!previewUrl && "Aucune photo"}
						</div>
						{!isDefault && (
							<div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
								<button className="mens-btn ghost small" onClick={handlePickPhoto} disabled={uploading}>
									{uploading ? "…" : (previewUrl ? "Changer" : "Ajouter")}
								</button>
								{previewUrl && (
									<button className="mens-btn ghost small" onClick={handleRemovePhoto} style={{ color: "#e57373" }}>
										Retirer
									</button>
								)}
								<input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display: "none" }} />
							</div>
						)}
					</div>

					{/* Colonne form */}
					<div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
						<div>
							<label className="group-settings-label">Nom</label>
							<input
								className="admin-broadcast-input"
								value={nom}
								onChange={(e) => setNom(e.target.value)}
								disabled={isDefault}
								maxLength={100}
							/>
						</div>
						<div>
							<label className="group-settings-label">Description</label>
							<textarea
								className="admin-broadcast-input"
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								disabled={isDefault}
								rows={3}
								maxLength={1000}
							/>
						</div>
						<div>
							<label className="group-settings-label">
								Temps de repos (secondes)
								{isDefault && (
									<span style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px", borderRadius: 4, background: "rgba(48,164,154,0.14)", color: "#30a49a", fontWeight: 700, letterSpacing: "0.04em", verticalAlign: 1 }}>
										AJUSTABLE
									</span>
								)}
							</label>
							<input
								type="number"
								className="admin-broadcast-input"
								value={tempsRepos}
								onChange={(e) => setTempsRepos(parseInt(e.target.value, 10) || 0)}
								min={0}
								max={600}
							/>
						</div>
					</div>
				</div>

				<div style={{ marginTop: 14 }}>
					<label className="group-settings-label">Groupes musculaires</label>
					<div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
						{MUSCLE_LABELS.map((label) => {
							const active = muscles.includes(label);
							return (
								<button
									key={label}
									type="button"
									onClick={() => toggleMuscle(label)}
									disabled={isDefault}
									style={{
										padding: "6px 10px",
										borderRadius: 16,
										border: `1px solid ${active ? "#30a49a" : "var(--line-soft)"}`,
										background: active ? "rgba(48,164,154,0.12)" : "transparent",
										color: active ? "#30a49a" : "var(--ink)",
										fontSize: 12,
										cursor: isDefault ? "not-allowed" : "pointer",
										opacity: isDefault ? 0.5 : 1,
									}}
								>
									{label}
								</button>
							);
						})}
					</div>
				</div>

				{error && <div className="mens-modal-error">{error}</div>}

				<div className="mens-modal-actions">
					<button className="mens-btn ghost" onClick={onClose} disabled={saving}>Annuler</button>
					<button className="mens-btn primary" onClick={handleSave} disabled={saving || uploading}>
						{saving ? "…" : (isEditing ? "Enregistrer" : "Créer")}
					</button>
				</div>
			</div>
		</div>
	);
}

// ─── Modale import depuis biblio coach ───
function ImportFromCoachModal({ studentId, coachExercises, studentExercises, onClose, onImported }) {
	const [q, setQ] = useState("");
	const [importingId, setImportingId] = useState(null);
	const [error, setError] = useState("");

	const studentNames = useMemo(() =>
		new Set((studentExercises || []).map((e) => (e.nomExercice || "").trim().toLowerCase())),
	[studentExercises]);

	const list = useMemo(() => {
		const query = q.trim().toLowerCase();
		return (coachExercises || [])
			.filter((e) => !query || (e.nomExercice || "").toLowerCase().includes(query))
			.map((e) => ({ ...e, _already: studentNames.has((e.nomExercice || "").trim().toLowerCase()) }));
	}, [coachExercises, q, studentNames]);

	const handleImport = async (ex) => {
		setImportingId(ex.id); setError("");
		const res = await importCoachExerciseToStudent(studentId, ex.id);
		setImportingId(null);
		if (res.success) onImported?.(ex);
		else setError(res.error || "Erreur");
	};

	return (
		<div className="mens-modal-overlay" onClick={onClose}>
			<div className="mens-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560, width: "94%" }}>
				<div className="mens-modal-header">
					<h3>Importer depuis ma bibliothèque</h3>
					<button className="mens-modal-close" onClick={onClose}>×</button>
				</div>
				<div className="editor-panel-exercises-search" style={{ marginTop: 10 }}>
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
					</svg>
					<input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher dans mes exercices" autoFocus />
					{q && <button className="editor-panel-exercises-clear" onClick={() => setQ("")}>×</button>}
				</div>
				{error && <div className="mens-modal-error">{error}</div>}
				<div style={{ maxHeight: 400, overflowY: "auto", marginTop: 12 }}>
					{list.length === 0 ? (
						<div className="coach-exercises-library-empty">Aucun résultat</div>
					) : list.map((ex) => (
						<button
							key={ex.id} type="button"
							disabled={ex._already || importingId === ex.id}
							onClick={() => handleImport(ex)}
							style={{
								display: "flex", alignItems: "center", gap: 10, width: "100%",
								padding: "8px 10px", marginBottom: 6, borderRadius: 8,
								border: "1px solid var(--line-soft)", background: ex._already ? "rgba(0,0,0,0.03)" : "transparent",
								color: "var(--ink)", cursor: ex._already ? "default" : "pointer",
								textAlign: "left", opacity: ex._already ? 0.55 : 1,
							}}
						>
							<ExerciseThumb exercise={ex} size={40} />
							<div style={{ flex: 1, minWidth: 0 }}>
								<div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{ex.nomExercice}</div>
								{musclesArrayToNames(ex.muscles || []).length > 0 && (
									<div style={{ fontSize: 11, color: "var(--ink-soft)" }}>
										{musclesArrayToNames(ex.muscles || []).join(" · ")}
									</div>
								)}
							</div>
							<span style={{ fontSize: 12, fontWeight: 600, color: ex._already ? "var(--ink-faint)" : "#30a49a" }}>
								{ex._already ? "Déjà présent" : (importingId === ex.id ? "…" : "Importer →")}
							</span>
						</button>
					))}
				</div>
				<div className="mens-modal-actions">
					<button className="mens-btn ghost" onClick={onClose}>Fermer</button>
				</div>
			</div>
		</div>
	);
}

// ─── Page principale ───
export default function CoachExercisesLibrary({ user, coachExercises, students, onRefreshCoach }) {
	// Sélecteur de contexte : null = biblio du coach, sinon = biblio de l'élève
	const [selectedStudentId, setSelectedStudentId] = useState(null);
	const [studentExercises, setStudentExercises] = useState([]);
	const [loadingStudent, setLoadingStudent] = useState(false);

	const [query, setQuery] = useState("");
	const [filterKey, setFilterKey] = useState("all");
	const [editingExercise, setEditingExercise] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [showImportModal, setShowImportModal] = useState(false);

	// Contexte cible : coach OR élève sélectionné
	const isStudentContext = selectedStudentId != null;
	const targetUserId = isStudentContext ? selectedStudentId : user.id;
	const currentList = isStudentContext ? studentExercises : (coachExercises || []);

	// Charge la biblio de l'élève quand on le sélectionne
	useEffect(() => {
		if (!isStudentContext) return;
		let cancelled = false;
		setLoadingStudent(true);
		getStudentExercises(selectedStudentId).then((res) => {
			if (cancelled) return;
			setStudentExercises(res.success ? (res.exercises || []) : []);
			setLoadingStudent(false);
		});
		return () => { cancelled = true; };
	}, [selectedStudentId, isStudentContext]);

	const refreshStudent = async () => {
		if (!isStudentContext) return;
		const res = await getStudentExercises(selectedStudentId);
		if (res.success) setStudentExercises(res.exercises || []);
	};

	const filtered = useMemo(() => {
		const q = (query || "").trim().toLowerCase();
		const filterObj = MUSCLE_FILTERS.find((f) => f.key === filterKey);
		const targetMuscles = new Set(filterObj?.muscles || []);
		return (currentList || []).filter((ex) => {
			if (q && !(ex.nomExercice || "").toLowerCase().includes(q)) return false;
			if (filterKey !== "all" && targetMuscles.size > 0) {
				const names = musclesArrayToNames(ex.muscles || []);
				if (!names.some((m) => targetMuscles.has(m))) return false;
			}
			return true;
		});
	}, [currentList, query, filterKey]);

	const handleOpenNew = () => { setEditingExercise(null); setShowModal(true); };
	const handleOpenEdit = (ex) => { setEditingExercise(ex); setShowModal(true); };
	const handleSaved = async () => {
		setShowModal(false); setEditingExercise(null);
		if (isStudentContext) await refreshStudent();
		else await onRefreshCoach?.();
	};

	const handleDelete = async (ex) => {
		if (!window.confirm(`Supprimer « ${ex.nomExercice} » ?`)) return;
		const res = await deleteExercise(targetUserId, ex.id);
		if (!res.success) {
			alert(res.error || "Erreur");
			return;
		}
		if (isStudentContext) await refreshStudent();
		else await onRefreshCoach?.();
	};

	const contextLabel = isStudentContext
		? students?.find((s) => s.id === selectedStudentId)?.prenom || "l'élève"
		: null;

	return (
		<div className="coach-exercises-library">
			<div className="coach-exercises-library-header">
				<div>
					<h2 style={{ margin: 0, fontSize: 22 }}>
						{isStudentContext ? `Exercices de ${contextLabel}` : "Mes exercices"}
					</h2>
					<div style={{ color: "var(--ink-soft)", fontSize: 13, marginTop: 4 }}>
						{(currentList || []).length} exercice{(currentList || []).length > 1 ? "s" : ""}
						{isStudentContext ? " dans sa bibliothèque" : " dans ta bibliothèque"}
					</div>
				</div>
				<div style={{ display: "flex", gap: 8 }}>
					{isStudentContext && (
						<button className="mens-btn ghost" onClick={() => setShowImportModal(true)}>
							Importer depuis ma bibliothèque
						</button>
					)}
					<button className="mens-btn primary" onClick={handleOpenNew}>+ Nouvel exercice</button>
				</div>
			</div>

			{/* Sélecteur de contexte : Ma bibliothèque + chaque élève */}
			<div className="coach-exercises-context-selector">
				<button
					className={`coach-exercises-context-pill ${!isStudentContext ? "active" : ""}`}
					onClick={() => setSelectedStudentId(null)}
				>
					Ma bibliothèque
				</button>
				{(students || []).map((s) => (
					<button
						key={s.id}
						className={`coach-exercises-context-pill ${selectedStudentId === s.id ? "active" : ""}`}
						onClick={() => setSelectedStudentId(s.id)}
					>
						{s.prenom} {s.nom}
					</button>
				))}
			</div>

			<div className="coach-exercises-library-toolbar">
				<div className="editor-panel-exercises-search" style={{ flex: 1 }}>
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<circle cx="11" cy="11" r="8" />
						<line x1="21" y1="21" x2="16.65" y2="16.65" />
					</svg>
					<input
						type="text"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						placeholder="Rechercher un exercice…"
					/>
					{query && <button className="editor-panel-exercises-clear" onClick={() => setQuery("")}>×</button>}
				</div>
				<div className="coach-exercises-library-filters">
					{MUSCLE_FILTERS.map((f) => (
						<button
							key={f.key}
							className={`editor-panel-templates-folderpill ${filterKey === f.key ? "active" : ""}`}
							onClick={() => setFilterKey(f.key)}
						>
							{f.label}
						</button>
					))}
				</div>
			</div>

			{loadingStudent ? (
				<div className="coach-exercises-library-empty">Chargement…</div>
			) : filtered.length === 0 ? (
				<div className="coach-exercises-library-empty">
					{query || filterKey !== "all" ? "Aucun exercice ne correspond." : "Aucun exercice pour l'instant."}
				</div>
			) : (
				<div className="coach-exercises-library-grid">
					{filtered.map((ex) => {
						const isDefault = isDefaultExercise(ex);
						const names = musclesArrayToNames(ex.muscles || []);
						return (
							<div key={ex.id} className="coach-exercises-library-card-wrap" style={{ position: "relative" }}>
								<button
									type="button"
									className="coach-exercises-library-card"
									onClick={() => handleOpenEdit(ex)}
									title={isDefault ? "Exercice de base — non modifiable" : "Modifier l'exercice"}
								>
									<ExerciseThumb exercise={ex} size={64} />
									<div style={{ flex: 1, minWidth: 0 }}>
										<div className="coach-exercises-library-card-name">
											{ex.nomExercice}
											{isDefault && (
												<span style={{ marginLeft: 6, fontSize: 10, padding: "1px 6px", borderRadius: 4, background: "rgba(0,0,0,0.06)", color: "var(--ink-faint)" }}>
													BASE
												</span>
											)}
										</div>
										{names.length > 0 && (
											<div className="coach-exercises-library-card-muscles">{names.join(" · ")}</div>
										)}
										{ex.tempsRepos > 0 && (
											<div style={{ fontSize: 11, color: "var(--ink-faint)", marginTop: 2 }}>Repos {ex.tempsRepos}s</div>
										)}
									</div>
								</button>
								{!isDefault && (
									<button
										type="button"
										onClick={(e) => { e.stopPropagation(); handleDelete(ex); }}
										title="Supprimer l'exercice"
										style={{
											position: "absolute", top: 6, right: 6,
											width: 26, height: 26, borderRadius: 7,
											background: "transparent",
											border: "1px solid rgba(244,67,54,0.30)",
											color: "rgba(244,67,54,0.80)",
											display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
										}}
									>
										<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
											<polyline points="3 6 5 6 21 6" />
											<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
										</svg>
									</button>
								)}
							</div>
						);
					})}
				</div>
			)}

			{showModal && (
				<ExerciseFormModal
					userId={targetUserId}
					exercise={editingExercise}
					onClose={() => { setShowModal(false); setEditingExercise(null); }}
					onSaved={handleSaved}
				/>
			)}

			{showImportModal && (
				<ImportFromCoachModal
					studentId={selectedStudentId}
					coachExercises={coachExercises}
					studentExercises={studentExercises}
					onClose={() => setShowImportModal(false)}
					onImported={async () => { await refreshStudent(); }}
				/>
			)}
		</div>
	);
}
